// src/contexts/OrgAssetConfigContext.jsx
// -----------------------------------------------------------------------------
// Centralized org-level asset configuration hydration for the DAM module.
// Responsibilities:
//  • Read organizations/{orgId} once (promotion config + optional label map)
//  • Resolve the org’s AUF (organizations/{orgId}.assetUploadFormId)
//     -> forms/{aufId}.fieldsMetadata (base64) -> decompress → normalize
//  • Cache results (in-memory + safeSetItem) with TTL to avoid redundant reads
//  • Expose a ready-made "registry" (static + AUF + providers) via getAssetTableRegistry
//  • Provide refresh() to force re-hydration
//
// Consumers (read-only):
//  • ColumnDraftManager.jsx → registry pills (no Firestore here)
//  • DraftSubmissionTable.jsx → may read promoted field names for nicer labels, if needed
//
// Notes:
//  • Provider accepts optional props for providerFlags/staticPolicy, orgId override.
//  • Uses useAuth().organizationId by default.
// -----------------------------------------------------------------------------

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { safeGetItem, safeSetItem } from '../utils/storageUtils';
import { decompressData } from '../utils/decompressionUtils';
import {
  getAssetTableRegistry,
  normalizeAufFields,
} from '../utils/assetTableRegistry';
import { useAuth } from './AuthContext';

// Public API ------------------------------------------------------------------
const OrgAssetConfigContext = createContext(null);
export const useOrgAssetConfig = () => useContext(OrgAssetConfigContext);

// Tunables --------------------------------------------------------------------
const CACHE_TTL_MS = 5 * 60 * 1000;          // 5 minutes in local/session storage
const MEMO_TTL_MS  = 60 * 1000;              // 1 minute in-memory short-circuit

// Optional: in-memory cache to short-circuit rapid remounts/switches
// Map<orgId, { ts:number, data: {...} }>
const MEM_CACHE = new Map();

/**
 * OrgAssetConfigProvider
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.organizationId]      // optional override (otherwise from AuthContext)
 * @param {object} [props.providerFlags]       // e.g. { bynder: true } when you wire providers
 * @param {object} [props.staticPolicy]        // e.g. { includeAssetId: false }
 * @param {number} [props.cacheTtlMs]          // override storage TTL
 * @param {number} [props.memoryTtlMs]         // override in-memory TTL
 */
export function OrgAssetConfigProvider({
  children,
  organizationId: orgIdProp,
  providerFlags = {},
  staticPolicy  = { includeAssetId: false },
  cacheTtlMs    = CACHE_TTL_MS,
  memoryTtlMs   = MEMO_TTL_MS,
}) {
  // Resolve org ID (prop wins; else from auth)
  const { organizationId: orgIdFromAuth } = useAuth() || {};
  const organizationId = orgIdProp ?? orgIdFromAuth ?? null;

  // State exposed to consumers
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [promotionConfig, setPromotionConfig] = useState({ keys: [], arrayKeys: [], dateKeys: [] });
  const [labelsMap, setLabelsMap]             = useState({}); // AUF UFI → label (if you materialize this per-org)
  const [aufFieldsRaw, setAufFieldsRaw]       = useState(null); // decompressed forms/{aufId}.fieldsMetadata
  const [aufFieldsNormalized, setAufFieldsNormalized] = useState([]); // [{ufi,label,type}]

  // For diagnostics
  const lastHydrateRef = useRef(0);

  // Storage cache key per org
  const cacheKey = useMemo(() => (organizationId ? `org_cfg_${organizationId}` : null), [organizationId]);

  // Internal: merge-safe promotion config
  const normPromotion = useCallback((pc) => ({
    keys: Array.isArray(pc?.keys) ? pc.keys : [],
    arrayKeys: Array.isArray(pc?.arrayKeys) ? pc.arrayKeys : [],
    dateKeys: Array.isArray(pc?.dateKeys) ? pc.dateKeys : [],
  }), []);

  // Hydration logic -----------------------------------------------------------
const hydrate = useCallback(async (opts = { force: false }) => {
  setError(null);

  if (!organizationId) {
    setPromotionConfig({ keys: [], arrayKeys: [], dateKeys: [] });
    setLabelsMap({});
    setAufFieldsRaw(null);
    setAufFieldsNormalized([]);
    lastHydrateRef.current = Date.now();
    return;
  }

  const now = Date.now();

// 1) Memory cache check — enterprise mode:
// We intentionally do not set state from memory cache to avoid
// showing stale AUF fields even for a split second.
// We still keep MEM_CACHE for a quick payload *copy* below if we
// confirm timestamps match (see storage / Firestore path).
//const memEntry = (!opts.force && MEM_CACHE.has(organizationId))
//  ? MEM_CACHE.get(organizationId)
//  : null;


  setLoading(true);

  try {
    // 2) Storage cache (do not early return yet; we may need to compare AUF timestamps)
    const cached = cacheKey ? safeGetItem(cacheKey) : null;
    const cacheFresh = !opts.force && cached?._ts && (now - cached._ts) < cacheTtlMs;

    // 3) Fetch org (always do this so we can resolve current AUF id & compare timestamps)
    const orgSnap = await getDoc(doc(db, 'organizations', organizationId));
    const org     = orgSnap.exists() ? orgSnap.data() : {};

    const nextPromotion = normPromotion(org?.assetPromotionConfig || {});
    const nextLabelsMap = org?.assetFilterCatalog?.labels || {};

    // 4) Resolve AUF and read *its* lastModified for cache busting
    const aufId = org?.assetUploadFormId || null;
    let aufLastModMs = null;

    let formSnap = null;
    if (aufId) {
      formSnap = await getDoc(doc(db, 'forms', aufId));
      if (formSnap.exists()) {
        const f = formSnap.data() || {};
        const lm = f.lastModified?.toMillis?.() ?? f.publishedAt?.toMillis?.() ?? null;
        aufLastModMs = typeof lm === 'number' ? lm : null;
      }
    }

    // Decide whether cached AUF is still valid
    const cachedAufId   = cached?._aufId ?? null;
    const cachedAufLmd  = typeof cached?._aufLastModified === 'number' ? cached._aufLastModified : null;
    const canReuseCache =
      cacheFresh &&
      cachedAufId &&
      cachedAufId === aufId &&
      cachedAufLmd &&
      aufLastModMs &&
      cachedAufLmd >= aufLastModMs;

    let aufRaw = null;
    let aufNorm = [];

    if (canReuseCache) {
      // ✅ AUF cache is fresh enough – reuse it
      aufRaw  = cached.aufFieldsRaw || null;
      aufNorm = Array.isArray(cached.aufFieldsNormalized)
        ? cached.aufFieldsNormalized
        : [];
    } else {
      // ❌ Cache stale or AUF changed – recompute from Firestore
      if (formSnap?.exists?.()) {
        const fieldsB64 = formSnap.data()?.fieldsMetadata || null;
        if (fieldsB64) {
          try {
            aufRaw  = await decompressData(fieldsB64);
            aufNorm = normalizeAufFields(aufRaw);
          } catch (e) {
            console.warn(
              '[OrgAssetConfig] Failed to decompress AUF fieldsMetadata (non-fatal):',
              e?.message || e
            );
          }
        }
      }
    }

    // 5) Set state
    setPromotionConfig(nextPromotion);
    setLabelsMap(nextLabelsMap);
    setAufFieldsRaw(aufRaw);
    setAufFieldsNormalized(aufNorm);
// 🔎 DIAG: AUF / registry inputs
console.log('[OrgAssetConfig] AUF hydration snapshot:', {
  organizationId,
  aufId,
  aufLastModMs,
  cacheFresh,
  canReuseCache,
  normCount: Array.isArray(aufNorm) ? aufNorm.length : 0,
  first5: Array.isArray(aufNorm) ? aufNorm.slice(0, 5) : [],
  promotionKeys: {
    keys: (nextPromotion?.keys || []).length,
    arrayKeys: (nextPromotion?.arrayKeys || []).length,
    dateKeys: (nextPromotion?.dateKeys || []).length,
  },
  labelsMapSize: Object.keys(nextLabelsMap || {}).length,
});

    lastHydrateRef.current = now;



    // 6) Persist fresh payload back to cache/memory for next time
    const payload = {
      _ts: now,
      _aufId: aufId || null,
      _aufLastModified: aufLastModMs,

      promotionConfig: nextPromotion,
      labelsMap: nextLabelsMap,
      aufFieldsRaw: aufRaw,
      aufFieldsNormalized: aufNorm,
    };
    if (cacheKey) safeSetItem(cacheKey, payload);
    MEM_CACHE.set(organizationId, { ts: now, data: payload });

  } catch (e) {
    console.error('[OrgAssetConfig] Hydration failed:', e?.message || e);
    setError(e?.message || String(e));
  } finally {
    setLoading(false);
  }
}, [organizationId, cacheKey, normPromotion, memoryTtlMs, cacheTtlMs]);


  // Initial and orgId-change hydration
  useEffect(() => {
    hydrate({ force: false });
  }, [hydrate]);

// Registry (UI-ready) -------------------------------------------------------
const registry = useMemo(() => {
  // Always expose Asset ID virtual in the registry (designer may toggle it),
  // while still allowing the provider's staticPolicy prop to override/extend.
  const mergedStaticPolicy = {
    includeAssetId: true,          // ← ensure Asset ID virtual is available
    ...(staticPolicy || {}),
  };

  // Build dynamic registry (static + all AUF fields + provider suggestions)
  // NOTE: you can pass providerFlags (e.g., { bynder:true }) as you wire integrations.
  return getAssetTableRegistry({
    organizationId,
    promotionConfig,
    labelsMap,
    aufFields: aufFieldsRaw,           // util will normalize if needed
    providerFlags,
    staticPolicy: mergedStaticPolicy,  // ← use merged policy
    filter: { includeFieldTypes: ['attach', 'image'] },
  });
}, [
  organizationId,
  promotionConfig,
  labelsMap,
  aufFieldsRaw,
  providerFlags,
  staticPolicy, // keep in deps since we merge it
]);

// 🔎 DIAG: registry shape (roles & counts)
// Insert this right after the registry useMemo above
useEffect(() => {
  if (!Array.isArray(registry)) return;

  const byRole = registry.reduce((acc, d) => {
    const k = d?.role || 'unknown';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const first10 = registry.slice(0, 10).map(d => ({
    key: d.key,
    role: d.role,
    ufi: d.ufi,
    promoted: !!d.promoted,
  }));

  console.log('[OrgAssetConfig] registry summary:', {
    size: registry.length,
    byRole,
    first10,
  });
}, [registry]);


  // Public context value ------------------------------------------------------
  const value = useMemo(() => ({
    organizationId,
    loading,
    error,

    // raw org config (useful for advanced UIs)
    promotionConfig,
    labelsMap,
    aufFieldsRaw,
    aufFieldsNormalized,

    // dynamic, UI-ready registry
    registry,

    // force reload (ignores cache)
    refresh: () => hydrate({ force: true }),

    // diagnostics
    _lastHydratedAt: lastHydrateRef.current || 0,
    _cacheTtlMs: cacheTtlMs,
    _memoryTtlMs: memoryTtlMs,
  }), [
    organizationId,
    loading,
    error,
    promotionConfig,
    labelsMap,
    aufFieldsRaw,
    aufFieldsNormalized,
    registry,
    hydrate,
    cacheTtlMs,
    memoryTtlMs,
  ]);

  return (
    <OrgAssetConfigContext.Provider value={value}>
      {children}
    </OrgAssetConfigContext.Provider>
  );
}
